import ImageFieldModal from '$tablet-app/src/app/components/ImageField/ImageFieldModal';
import Text from '$tablet-app/src/app/components/Text';

import useRecordImage from '$tablet-app/src/app/images/hooks/useRecordImage';
import { LocalImage } from '$tablet-app/src/app/images/types';
import { palette } from '$tablet-app/src/app/theme';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { DashboardWidgetImage } from '@akin-home/dashboards';
import { useHabitat } from '@akin-home/habitats/react';
import { postImageToCloud } from '@akin-home/images/frontend';
import { useGetImage, useImageUrl } from '@akin-home/images/react';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import styled from 'styled-components/native';
import { useStateStore } from '../../stores/useStateStore';
import DashboardPanel from '../DashboardPanel';

interface Props {
  widget: DashboardWidgetImage;
}

const ImageDashboardPanel = (props: Props) => {
  const { widget } = props;

  const { habitat } = useHabitat();
  const { uri: habitat_image_uri } = useRecordImage({ record: habitat });
  const updateWidget = useStateStore((state) => state.updateDashboardWidget);

  /** Until we support multiple images, just get the first */
  const image_data = widget.data?.images?.[0];
  const { data: get_image_data } = useGetImage({ id: image_data?.image_id });
  const { data: image_uri } = useImageUrl({ image: get_image_data });

  const [imagePickerVisible, showImagePicker, hideImagePicker] = useBool();

  const onSubmit = async (data: { url?: string; imageUpload?: LocalImage }) => {
    if (data.imageUpload) {
      updateWidget({
        ...widget,

        data: {
          images: [
            {
              image_id: (
                await postImageToCloud({
                  file: data.imageUpload,
                  habitatID: habitat.habitatID,
                })
              ).id,
            },
          ],
        },
      });
    } else if (data.url) {
      updateWidget({
        ...widget,
        data: {
          images: [
            {
              image_url: data.url,
            },
          ],
        },
      });
    }

    hideImagePicker();
  };

  /** Get uri from image_uri, image_url or habitat image */
  const uri = image_uri || image_data?.image_url || habitat_image_uri;

  return (
    <Panel>
      <Touchable onPress={showImagePicker}>
        {uri ? (
          <PanelImage source={{ uri }} />
        ) : (
          <Placeholder>
            <IconWrapper>
              <FontAwesomeIcon icon="camera" size={24} />
            </IconWrapper>
            <Text bold>Click here to choose an image</Text>
          </Placeholder>
        )}
      </Touchable>

      {imagePickerVisible ? (
        <ImageFieldModal
          onCancel={hideImagePicker}
          onSubmit={onSubmit}
          publicUrl={habitat.imageUrl}
        />
      ) : null}
    </Panel>
  );
};

const Panel = styled(DashboardPanel)`
  flex: 1;
  overflow: hidden;
`;

const Touchable = styled.TouchableOpacity`
  height: 100%;
  width: 100%;
`;

const PanelImage = styled.Image`
  height: 100%;
  width: 100%;
`;

const Placeholder = styled.View`
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const IconWrapper = styled.View`
  height: 80px;
  width: 80px;
  margin-bottom: 20px;
  border-radius: 40px;
  background-color: ${palette.Background_blue};
  justify-content: center;
  align-items: center;
`;

export default ImageDashboardPanel;
